import { createBrowserRouter, Navigate } from 'react-router-dom';
import { lazy, Suspense } from 'react';
import { AdaptiveLayout } from './components/AdaptiveLayout';
import { AdminLayout } from './components/AdminLayout';
import { NoLayout } from './components/NoLayout';
import { RootLayout } from './components/RootLayout';
import { QuestionRoute } from './components/PostSheet/QuestionRoute';
import { Home } from './screens/Home';
import { isNativePlatform } from '@lib/platform';
import { AUTH_DOOR_PATH, AUTH_LINK_PATH } from '@lib/magicLink/authLinkParams';
import { CALLBACK_PATH } from '@lib/magicLink/staticTransitShell';
import { OnboardingPosterFallback } from './screens/Onboarding/OnboardingPosterFallback';
import { CHAT_ABOUT_POST_PATH, routes } from '@routes';

// Register custom share configurations (event/campaign-specific overrides)
import './config/shareCustomizations';

// Centralized providers
import { ProtectedRoute } from '@providers';

// Lazy load screens for code splitting
const Events = lazy(() => import('./screens/Events').then(m => ({ default: m.Events })));
const Messages = lazy(() => import('./screens/Messages').then(m => ({ default: m.Messages })));
const Notifications = lazy(() =>
  import('./screens/Notifications').then(m => ({ default: m.Notifications })),
);
const CreatePoll = lazy(() =>
  import('./screens/CreatePoll').then(m => ({
    default: m.CreatePollRoute,
  })),
);
// TECH-867: the live poll's host view (FE-L1b) and Your live polls.
const LivePollsList = lazy(() =>
  import('./screens/live/LivePollsList').then(m => ({ default: m.LivePollsList })),
);
const LiveHostView = lazy(() =>
  import('./screens/live/LiveHostView').then(m => ({ default: m.LiveHostView })),
);
const CreateQuestion = lazy(() =>
  import('./screens/CreateQuestion').then(m => ({
    default: m.CreateQuestion,
  })),
);
const CreateEvent = lazy(() =>
  import('./screens/CreateEvent').then(m => ({ default: m.CreateEvent })),
);
const EventDetails = lazy(() =>
  import('./screens/EventDetails').then(m => ({ default: m.EventDetails })),
);
const PublicLayout = lazy(() =>
  import('./components/PublicLayout').then(m => ({ default: m.PublicLayout })),
);
const QuestionDetails = lazy(() =>
  import('./screens/QuestionDetails').then(m => ({
    default: m.QuestionDetails,
  })),
);
const ProfilePage = lazy(() =>
  import('./screens/ProfilePage').then(m => ({ default: m.ProfilePage })),
);
const ProfileEditFlow = lazy(() =>
  import('./screens/ProfileEditFlow').then(m => ({
    default: m.ProfileEditFlow,
  })),
);
const Chat = lazy(() => import('./screens/Chat').then(m => ({ default: m.Chat })));
const Settings = lazy(() => import('./screens/Settings').then(m => ({ default: m.Settings })));
const NotificationTemplatesAdmin = lazy(() =>
  import('./screens/settings/notification-templates/NotificationTemplatesAdmin').then(m => ({
    default: m.NotificationTemplatesAdmin,
  })),
);
const NotificationTemplateFormAdmin = lazy(() =>
  import('./screens/settings/notification-templates/NotificationTemplateFormAdmin').then(m => ({
    default: m.NotificationTemplateFormAdmin,
  })),
);
const NotificationSettingsAdmin = lazy(() =>
  import('./screens/settings/notification-settings-admin/NotificationSettingsAdmin').then(m => ({
    default: m.NotificationSettingsAdmin,
  })),
);
const IcpSearchAdmin = lazy(() =>
  import('./screens/IcpSearchAdmin').then(m => ({ default: m.IcpSearchAdmin })),
);
const AdminAnalytics = lazy(() =>
  import('./screens/settings/AdminAnalytics').then(m => ({ default: m.AdminAnalytics })),
);
const AdminMeetverse = lazy(() =>
  import('./screens/settings/AdminMeetverse').then(m => ({ default: m.AdminMeetverse })),
);
const ReportsAdmin = lazy(() =>
  import('./screens/settings/ReportsAdmin').then(m => ({ default: m.ReportsAdmin })),
);
// AIDEV-NOTE: lazy() does not trigger the import until rendered. On native, the /callback route
// is excluded below, so this module is never loaded. Keeping the declaration unconditional avoids
// TS narrowing issues with LazyExoticComponent | undefined in JSX context.
const Callback = lazy(() => import('./screens/Callback').then(m => ({ default: m.Callback })));
const LogoutCallback = lazy(() =>
  import('./screens/LogoutCallback').then(m => ({ default: m.LogoutCallback })),
);
const SignupFlow = lazy(() =>
  import('./screens/signup/SignupFlow').then(m => ({ default: m.SignupFlow })),
);
const Logout = lazy(() => import('./screens/Logout').then(m => ({ default: m.Logout })));
const AuthDoor = lazy(() => import('./screens/AuthDoor').then(m => ({ default: m.AuthDoor })));
const AuthLinkLanding = lazy(() =>
  import('./screens/AuthLinkLanding').then(m => ({ default: m.AuthLinkLanding })),
);
const CommunityDetail = lazy(() =>
  import('./screens/CommunityDetail').then(m => ({ default: m.CommunityDetail })),
);
const SpaceWelcome = lazy(() =>
  import('./screens/SpaceWelcome').then(m => ({ default: m.SpaceWelcome })),
);
const InvitePage = lazy(() =>
  import('./screens/InvitePage').then(m => ({ default: m.InvitePage })),
);
const SpaceWelcomeAliasRedirect = lazy(() =>
  import('./screens/SpaceWelcomeAliasRedirect').then(m => ({
    default: m.SpaceWelcomeAliasRedirect,
  })),
);
const Onboarding = lazy(() =>
  import('./screens/Onboarding/Onboarding').then(m => ({ default: m.Onboarding })),
);
const CreateSpace = lazy(() =>
  import('./screens/CreateSpace').then(m => ({ default: m.CreateSpace })),
);
const SetupSpace = lazy(() =>
  import('./screens/SetupSpace').then(m => ({ default: m.SetupSpace })),
);
const ManageTopics = lazy(() =>
  import('./screens/ManageTopics').then(m => ({ default: m.ManageTopics })),
);
const ManagePartnerships = lazy(() =>
  import('./screens/ManagePartnerships').then(m => ({ default: m.ManagePartnerships })),
);
const AdminInviteScreen = lazy(() =>
  import('./screens/AdminInviteScreen').then(m => ({ default: m.AdminInviteScreen })),
);
const SpaceAnalytics = lazy(() =>
  import('./screens/settings/SpaceAnalytics').then(m => ({ default: m.SpaceAnalytics })),
);
const ActivationKit = lazy(() => import('./screens/kit').then(m => ({ default: m.ActivationKit })));
const KitLegacyRedirect = lazy(() =>
  import('./screens/kit').then(m => ({ default: m.KitLegacyRedirect })),
);
const NotFound = lazy(() => import('./screens/NotFound'));
const LiveBigScreen = lazy(() =>
  import('./screens/live/LiveBigScreen').then(m => ({ default: m.LiveBigScreen })),
);
// The live link (the QR): its gate draws the Post sheet (vaul), so it loads on its own.
const LiveRoute = lazy(() =>
  import('./components/PostSheet/LiveRoute').then(m => ({ default: m.LiveRoute })),
);

// AIDEV-NOTE [MEETBALL magic-link]: createBrowserRouter runs at MODULE SCOPE and
// snapshots `window.location` right here, when this module is first imported.
// `history.replaceState` fires no popstate, so anything stripped from the URL
// after this line still reaches `useLocation()` / `useSearchParams()` for the
// life of the document. src/index.tsx's FIRST import must therefore stay
// './lib/magicLink/bootstrapAuthLinkParams', which strips the magic-link token
// and email before this snapshot is taken. Do not reorder it.
export const router = createBrowserRouter([
  {
    element: <RootLayout />,
    children: [
      // AIDEV-NOTE: Root uses the flag-gated landing (featureFlags.landOnEvents). /hi stays
      // on /home: it is the auth-recovery fallback (useInvalidGrantHandler) and /home is the
      // guest-friendly re-entry point.
      { path: '/', element: <Navigate to={routes.landing()} replace /> },
      { path: '/hi', element: <Navigate to={routes.home()} replace /> },

      // AIDEV-NOTE: NoLayout Routes - No header/footer chrome
      // AIDEV-NOTE: /callback excluded on native -- Capacitor SDK handles OAuth callback
      // via deep link internally; useNativePostAuth handles post-auth redirect logic
      {
        element: <NoLayout />,
        children: [
          { path: '/signup', element: <SignupFlow /> },
          // The single door: email magic link + social. Reached directly and
          // from the auth UI's expired-link bounce (?email=…&from=expired).
          //
          // AIDEV-NOTE [MEETBALL magic-link]: all three auth paths come from the
          // constants rather than from literals, and the point is not tidiness.
          // "Which paths are auth routes" is stated in several notations that no
          // module imports -- the inline referrer gate and transit-shell gate in
          // index.html, the `Referrer-Policy` blocks in public/_headers, the
          // service-worker denylist pattern -- and six of the seven fail
          // QUIETLY. Miss this table and you get a 404, which is loud. Miss the
          // header rule and a bearer token lands in an edge access log with
          // nothing red anywhere. Consuming the constants here is what makes
          // this file agree with `AUTH_ROUTE_PATHS`, which
          // `authReferrerGate.test.ts` then holds the edge rules to.
          { path: AUTH_DOOR_PATH, element: <AuthDoor /> },
          // The magic-link landing. index.tsx has already captured and stripped
          // ?token=/?email= before this lazy chunk is even requested.
          { path: AUTH_LINK_PATH, element: <AuthLinkLanding /> },
          ...(!isNativePlatform() ? [{ path: CALLBACK_PATH, element: <Callback /> }] : []),
          { path: '/logout-callback', element: <LogoutCallback /> },
          { path: '/logout', element: <Logout /> },
        ],
      },

      // AIDEV-NOTE: AdaptiveLayout Routes - Selects GuestLayout or StandardLayout based on auth
      {
        element: <AdaptiveLayout />,
        children: [
          // AIDEV-NOTE: /home is accessible by both guests and authenticated users
          { path: '/home', element: <Home /> },
          {
            path: '/messages',
            element: (
              <ProtectedRoute>
                <Messages />
              </ProtectedRoute>
            ),
          },
          {
            path: '/notifications',
            element: (
              <ProtectedRoute>
                <Notifications />
              </ProtectedRoute>
            ),
          },
        ],
      },

      // AIDEV-NOTE: PublicLayout Routes - Guest users can access these screens
      {
        element: <PublicLayout />,
        children: [
          { path: '/events', element: <Events /> },
          { path: '/events/:slugOrId', element: <EventDetails /> },
          { path: '/communities/:slugOrId', element: <CommunityDetail /> },
        ],
      },

      // AIDEV-NOTE: NoLayout for create-question - same UI for guests and authenticated users
      {
        element: <NoLayout />,
        children: [
          { path: '/create-question', element: <CreateQuestion /> },
          // TECH-197 FE-2b: the Poll composer, signed in only (a poll is never a guest's).
          {
            path: '/create-poll',
            element: (
              <ProtectedRoute>
                <CreatePoll />
              </ProtectedRoute>
            ),
          },
          // TECH-867 H5: Your live polls, the author's list.
          {
            path: '/live',
            element: (
              <ProtectedRoute>
                <LivePollsList />
              </ProtectedRoute>
            ),
          },
          // TECH-867: the live poll's host view, the author's only.
          {
            path: '/live/:code/host',
            element: (
              <ProtectedRoute>
                <LiveHostView />
              </ProtectedRoute>
            ),
          },
        ],
      },

      // AIDEV-NOTE: Space welcome — shareable full-screen intro landing. Guest-accessible
      // (no ProtectedRoute); the join CTA itself gates auth, mirroring CommunityDetail.
      {
        element: <NoLayout />,
        children: [
          // Canonical welcome now renders the Onboarding flow (was V2). Existing
          // share/QR links, the guest redirect, and routes.community.welcome() all
          // target this path.
          {
            path: '/communities/:slugOrId/welcome',
            element: (
              <Suspense fallback={<OnboardingPosterFallback />}>
                <Onboarding />
              </Suspense>
            ),
          },
          // V1 parked here, kept for later (not deleted).
          { path: '/communities/:slugOrId/welcome-legacy', element: <SpaceWelcome /> },
          // Temporary dev alias → canonical /welcome (preserves slug + query).
          { path: '/communities/:slugOrId/welcome_2', element: <SpaceWelcomeAliasRedirect /> },
        ],
      },

      // AIDEV-NOTE: Personal invite landing - guest-accessible (no ProtectedRoute);
      // the signup CTA gates auth, and logged-in visitors are redirected to /home.
      {
        element: <NoLayout />,
        children: [{ path: '/invite/:code', element: <InvitePage /> }],
      },

      // TECH-867: the live poll's big screen. Public, no sign-in: the screen link's key shows it, display only.
      {
        element: <NoLayout />,
        children: [{ path: '/live/:code/screen', element: <LiveBigScreen /> }],
      },

      // TECH-867 FE-L3a: the live link (the QR). Public: the live poll in the Post sheet over its Space, its
      // Event page or a neutral backdrop (LiveRoute decides); read only through the room's /live routes.
      {
        element: <NoLayout />,
        children: [{ path: '/live/:code', element: <LiveRoute /> }],
      },

      // AIDEV-NOTE: Organizer Activation Kit — public, slug-driven, no-chrome.
      // A public Space's kit is open signed out. Invite-only and private Spaces reuse the Space page's sign-in and membership gates; members see the kit.
      // Lives at /communities/:slug/kit; the old /kit/:slug redirects there with
      // its hash and query. /kit alone is the paste-your-link gate.
      {
        element: <NoLayout />,
        children: [
          { path: '/kit', element: <ActivationKit /> },
          { path: '/kit/:spaceSlug', element: <KitLegacyRedirect /> },
          { path: '/communities/:spaceSlug/kit', element: <ActivationKit /> },
        ],
      },

      // AIDEV-NOTE: NoLayoutChrome Routes - Full-screen protected routes
      {
        element: <NoLayout />,
        children: [
          // AIDEV-NOTE: Guest access enabled - QuestionDetails handles auth internally.
          // QuestionRoute is the post page unless featureFlags.postSheet: then a poll opened
          // cold gets the Post sheet over its Space (in-app taps never reach this element:
          // PostSheetOutlet draws the sheet over the feed). See src/components/PostSheet.
          {
            path: '/questions/:slugOrId',
            element: <QuestionRoute postPage={<QuestionDetails />} />,
          },
          {
            path: '/questions/:slugOrId/edit',
            element: (
              <ProtectedRoute>
                <CreateQuestion />
              </ProtectedRoute>
            ),
          },
          {
            path: '/chat/:slugOrId',
            element: (
              <ProtectedRoute>
                <Chat />
              </ProtectedRoute>
            ),
          },
          {
            // A chat with the author of someone else's post, addressed by the post (TECH-693).
            path: CHAT_ABOUT_POST_PATH,
            element: (
              <ProtectedRoute>
                <Chat />
              </ProtectedRoute>
            ),
          },
          {
            path: '/profile/:slugOrId',
            element: (
              <ProtectedRoute>
                <ProfilePage />
              </ProtectedRoute>
            ),
          },
          {
            path: '/profile/:slugOrId/edit',
            element: (
              <ProtectedRoute>
                <ProfileEditFlow />
              </ProtectedRoute>
            ),
          },
          {
            path: '/create-event',
            element: (
              <ProtectedRoute>
                <CreateEvent />
              </ProtectedRoute>
            ),
          },
          {
            path: '/events/:slugOrId/edit',
            element: (
              <ProtectedRoute>
                <CreateEvent />
              </ProtectedRoute>
            ),
          },
          {
            path: '/create-community',
            element: (
              <ProtectedRoute>
                <CreateSpace />
              </ProtectedRoute>
            ),
          },
          {
            path: '/communities/:slugOrId/setup',
            element: (
              <ProtectedRoute>
                <SetupSpace />
              </ProtectedRoute>
            ),
          },
          {
            path: '/communities/:slugOrId/manage-topics',
            element: (
              <ProtectedRoute>
                <ManageTopics />
              </ProtectedRoute>
            ),
          },
          {
            path: '/communities/:slugOrId/manage-partnerships',
            element: (
              <ProtectedRoute>
                <ManagePartnerships />
              </ProtectedRoute>
            ),
          },
          {
            path: '/communities/:slugOrId/invite',
            element: (
              <ProtectedRoute>
                <AdminInviteScreen />
              </ProtectedRoute>
            ),
          },
          {
            path: '/communities/:slugOrId/statistics',
            element: (
              <ProtectedRoute>
                <SpaceAnalytics />
              </ProtectedRoute>
            ),
          },
          {
            path: '/settings',
            element: (
              <ProtectedRoute>
                <Settings />
              </ProtectedRoute>
            ),
          },
        ],
      },

      // AIDEV-NOTE: AdminLayout Routes - Full-width screens for admin tools
      // AdminLayout applies ProtectedRoute + AdminRoute guards and overrides
      // --app-max-width so admin tables/forms aren't cramped at 640px.
      {
        element: <AdminLayout />,
        children: [
          {
            path: '/settings/admin/notification-templates',
            element: <NotificationTemplatesAdmin />,
          },
          // AIDEV-NOTE: /new and /:id/edit share one unkeyed element: a landed create keeps its typing (templateRoutes.test)
          {
            path: '/settings/admin/notification-templates/new',
            element: <NotificationTemplateFormAdmin />,
          },
          {
            path: '/settings/admin/notification-templates/:id/edit',
            element: <NotificationTemplateFormAdmin />,
          },
          {
            path: '/settings/admin/notification-settings',
            element: <NotificationSettingsAdmin />,
          },
          {
            path: '/settings/admin/icp-search',
            element: <IcpSearchAdmin />,
          },
          {
            path: '/settings/admin/analytics',
            element: <AdminAnalytics />,
          },
          {
            path: '/settings/admin/meetverse',
            element: <AdminMeetverse />,
          },
          {
            path: '/settings/admin/reports',
            element: <ReportsAdmin />,
          },
        ],
      },

      // AIDEV-NOTE: Catch-all 404 route - must be last, uses AdaptiveLayout for proper chrome
      {
        element: <AdaptiveLayout />,
        children: [{ path: '*', element: <NotFound /> }],
      },
    ],
  },
]);
